import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { RootState } from '../../../../../../../@types/RootState';
import { Layout } from '../../../../../../../components';
import { AlertWithTimestamp } from '../../../../../../../components/AlertWithTimestamp';
import { resetCattegory } from '../../../../../../../reducers/ipa/limits/filters/cattegory';
import { resetSegment } from '../../../../../../../reducers/ipa/limits/filters/segment';
import { resetStore } from '../../../../../../../reducers/ipa/limits/filters/store';
import RegisterLimitFormActions from '../../components/RegisterLimitFormActions/RegisterLimitFormActions';
import RegisterLimitPageFilters from '../../components/RegisterLimitPageFilters/RegisterLimitPageFilters';
import RegisterLimitPageForm from '../../components/RegisterLimitPageForm/RegisterLimitPageForm';
import RegisterLimitPageHeader from '../../components/RegisterLimitPageHeader/RegisterLimitPageHeader';
import { getRegisterLimitModel } from '../../lib/data';
import { registerLimitSchema, RegisterLimitFormValues, RegisterLimitSchemaProps } from '../../lib/schema';
import { checkIsSegmentEmpty } from '../../lib/utils';
import { extractApiErrorMessage } from '../../../../../../../utils/extractApiErrorMessage';
import styles from './RegisterLimitPage.module.scss';
import { saveLimit } from './service';

type RegisterLimitPageProps = RouteComponentProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

const hasFormValue = (value: string | null | undefined) => value !== null && value !== undefined && value.trim() !== '';

const RegisterLimitPage = ({ filters, history, clearFilters }: RegisterLimitPageProps) => {
  const form = useForm<RegisterLimitFormValues, unknown, RegisterLimitSchemaProps>({
    resolver: zodResolver(registerLimitSchema),
    defaultValues: {
      name: '',
      marginType: 'margin',
      adjustPricesAccordingToLimits: false,
      minMargin: null,
      maxMargin: null,
      downwardVariation: null,
      upwardVariation: null,
      minCpi: null,
      maxCpi: null,
    },
  });

  const { marginType, minMargin, maxMargin, downwardVariation, upwardVariation, minCpi, maxCpi } = form.watch();
  const marginValues = marginType === 'objectiveMargin' ? [downwardVariation, upwardVariation] : [minMargin, maxMargin];
  const hasAnyLimit = [...marginValues, minCpi, maxCpi].some(hasFormValue);

  const handleSubmit = useCallback(
    async (formData: RegisterLimitSchemaProps) => {
      try {
        const model = getRegisterLimitModel({ formData, filters });
        const { status } = await saveLimit(model);
        if (![200, 201].includes(status)) return;
        clearFilters();
        form.reset();
        history.push('/ipa/limites-preco');
      } catch (err) {
        const message = extractApiErrorMessage(err) || 'Erro ao salvar limite. Tente novamente.';
        AlertWithTimestamp({ message });
      }
    },
    [filters, clearFilters, form, history],
  );

  const isSegmentEmpty = useMemo(() => {
    return checkIsSegmentEmpty(filters.cattegory.value);
  }, [filters.cattegory.value]);

  const isStoreEmpty = useMemo(() => {
    const { selectType, data } = filters.store;
    if (selectType === 'individual-store') {
      return !data.value['individual-store']?.length;
    }
    if (selectType === 'clusters') {
      return !data.value['clusters']?.length;
    }
    return false;
  }, [filters.store]);

  const isDisabledSubmit = useMemo(
    () => isSegmentEmpty || isStoreEmpty || form.formState.isSubmitting || !hasAnyLimit,
    [hasAnyLimit, isSegmentEmpty, isStoreEmpty, form.formState.isSubmitting],
  );

  useEffect(() => {
    clearFilters();
  }, [clearFilters]);

  return (
    <Layout className={styles.wrapper}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Layout.Body className={styles.body}>
          <RegisterLimitPageHeader form={form} />
          <RegisterLimitPageFilters />
          <RegisterLimitPageForm form={form} />
          <RegisterLimitFormActions isDisabledSubmit={isDisabledSubmit} />
        </Layout.Body>
      </form>
    </Layout>
  );
};

const mapStateToProps = (state: RootState) => ({
  filters: state.ipa.limits.filters,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  clearFilters: () => {
    dispatch(resetSegment());
    dispatch(resetStore());
    dispatch(resetCattegory());
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RegisterLimitPage));
